import {useCallback, useEffect, useMemo, useState} from 'react';
import {onValue, ref} from 'firebase/database';
import {db} from '../utils/firebaseConfig';
import {useAuth} from '../contexts/AuthContext';
import {Room} from '../types/room';
import {UtilityRecord} from '../types/utilityRecord';
import {getCurrentYearMonth, shiftYearMonth} from '../utils/helpers';
import {deriveTenantNotifications} from '../utils/notificationRules';

/**
 * Chủ nhà vừa làm gì với phòng của tôi - nguồn cho chuông trên TenantLayout.
 *
 * Giống useNotifications bên chủ nhà: KHÔNG có node `notifications` trong DB,
 * mọi mục suy ra từ trạng thái thật của bản ghi điện nước và hồ sơ phòng, nên
 * không bao giờ lệch với thực tế và không ai phải đi dọn thông báo cũ. Việc suy
 * ra nằm ở utils/notificationRules.ts - dùng chung với bộ gửi push.
 *
 * Khác một chỗ quan trọng: danh sách bên này có cả việc ĐÃ XONG ("đã thu tiền"),
 * không chỉ việc đang chờ. Nếu chấm đỏ đếm hết mọi mục thì nó sáng suốt từ lúc
 * chủ nhà xác nhận tới hết tháng. Nên chấm đỏ đếm mục CHƯA XEM, lưu ở
 * localStorage theo email - không đáng ghi vào DB, và mỗi người tự có trạng thái
 * đã xem trên máy mình.
 */

/** Giữ vừa đủ id để không phình localStorage; danh sách thật chỉ vài mục */
const SEEN_LIMIT = 50;

const seenStorageKey = (email: string) => `nha48:seenNotifications:${email}`;

const readSeen = (email: string | null | undefined): string[] => {
    if (!email) return [];
    try {
        const raw = window.localStorage.getItem(seenStorageKey(email));
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const useTenantNotifications = () => {
    const {roomId, role, user} = useAuth();
    const isTenant = role === 'tenant';
    const email = user?.email ?? null;

    const [room, setRoom] = useState<Room | null>(null);
    const [records, setRecords] = useState<Record<string, UtilityRecord | null>>({});
    const [seen, setSeen] = useState<string[]>(() => readSeen(email));

    const thisMonth = getCurrentYearMonth();
    const prevMonth = shiftYearMonth(thisMonth, -1);

    // Đổi tài khoản trên cùng một máy thì trạng thái "đã xem" phải theo người mới
    useEffect(() => setSeen(readSeen(email)), [email]);

    useEffect(() => {
        if (!isTenant || !roomId) return;
        return onValue(
            ref(db, `rooms/${roomId}`),
            snapshot => setRoom(snapshot.exists() ? {id: roomId, ...snapshot.val()} : null),
            () => setRoom(null),
        );
    }, [isTenant, roomId]);

    const contractStartMonth = room?.contractStartMonth;

    // Tháng này + tháng trước. Tháng trước là cần: chủ nhà thường chốt và thu
    // tiền tháng cũ vào đầu tháng mới, thông báo phải đuổi kịp việc đó.
    useEffect(() => {
        if (!isTenant || !roomId) return;

        // Rules chỉ cho người thuê đọc từ tháng bắt đầu hợp đồng trở đi. Lọc
        // trước ở đây để đừng bắn một request chắc chắn bị từ chối.
        const months = [thisMonth, prevMonth]
            .filter(month => !contractStartMonth || month >= contractStartMonth);

        const unsubscribes = months.map(month => onValue(
            ref(db, `utilityRecords/${roomId}/${month}`),
            snapshot => setRecords(prev => ({...prev, [month]: snapshot.exists() ? snapshot.val() : null})),
            () => setRecords(prev => ({...prev, [month]: null})),
        ));

        return () => unsubscribes.forEach(unsubscribe => unsubscribe());
    }, [isTenant, roomId, contractStartMonth, thisMonth, prevMonth]);

    const notifications = useMemo(
        () => deriveTenantNotifications({
            room: isTenant ? room : null,
            records,
            thisMonth,
            prevMonth,
            now: Date.now(),
        }),
        [isTenant, room, records, thisMonth, prevMonth],
    );

    const unseenCount = useMemo(
        () => notifications.filter(notification => !seen.includes(notification.id)).length,
        [notifications, seen],
    );

    const markAllSeen = useCallback(() => {
        if (!email || notifications.length === 0) return;
        setSeen(prev => {
            const next = Array.from(new Set([...prev, ...notifications.map(n => n.id)])).slice(-SEEN_LIMIT);
            try {
                window.localStorage.setItem(seenStorageKey(email), JSON.stringify(next));
            } catch {
                // Trình duyệt chặn localStorage (chế độ riêng tư): chấp nhận mất
                // trạng thái đã xem, không có gì để báo cho người dùng.
            }
            return next;
        });
    }, [email, notifications]);

    return {notifications, unseenCount, markAllSeen};
};
